/**
 * Format a date value as mm/dd/yyyy (zero-padded).
 * Handles YYYY-MM-DD strings, Date objects, and ISO strings.
 */
export const formatDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
  }
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const y = date.getFullYear();
    return `${m}/${d}/${y}`;
  } catch {
    return null;
  }
};

/**
 * Format a date-time value as mm/dd/yyyy, h:mm a (e.g., 03/09/2025, 2:30 PM).
 */
export const formatDateTime = (value) => {
  const datePart = formatDate(value);
  if (!datePart) return null;
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return datePart;
    const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart}, ${timePart}`;
  } catch {
    return datePart;
  }
};

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const HAS_TZ_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Server timestamps (created_at, changed_at, ...) are naive UTC with no offset
 * marker, which JS would otherwise parse as local. Attach the marker so the
 * value becomes a real instant.
 * Returns null for date-only or non-datetime input, which has no instant to shift.
 */
export const parseServerUtc = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!ISO_DATETIME_RE.test(text)) return null;
  const date = new Date(HAS_TZ_RE.test(text) ? text : `${text.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Format a UTC server timestamp as mm/dd/yyyy in the viewer's time zone. */
export const formatUtcDate = (value) => formatDate(parseServerUtc(value) ?? value);

/** Format a UTC server timestamp as mm/dd/yyyy, h:mm a in the viewer's time zone. */
export const formatUtcDateTime = (value) => formatDateTime(parseServerUtc(value) ?? value);
